import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
} from '@mui/material';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { usePatchedDefaultData } from '@work4all/components';
import { CardWidget } from '@work4all/components/lib/dataDisplay/card-widget';

import { useDataProvider, useNavigate } from '@work4all/data';
import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';

import { DataRequest } from '@work4all/models/lib/DataProvider';

import { mutationEntityToViewEntity } from '@work4all/utils';

import { HomeWidget } from '../../../../settings/validation/HomeWidget';

import {
  widgetColsByEntity,
  widgetDataByEntity,
} from './default-entity-widget-data';
import { widgetSortOptionsByEntity } from './default-entity-widget-sort-options';
import { useEntityWidgetFilterAndSearchParams } from './useEntityWidgetFilterAndSearchParams';

export const EntityWidget = (
  props: HomeWidget & {
    onDeleteClick?: () => void;
    onEditClick?: () => void;
    pageSize?: number;
    onCollapsedChange?: (value: boolean) => void;
    collapsible?: boolean;
    collapsed?: boolean;
  }
) => {
  const { t } = useTranslation();

  const {
    pageSize,
    definition,
    onEditClick,
    onDeleteClick,
    title,
    collapsed,
    onCollapsedChange,
  } = props;

  const { filter, searchParams } =
    useEntityWidgetFilterAndSearchParams(definition);

  // We are doing this for backward compatibility for entities like `inboundInvoice`.
  const viewEntity = mutationEntityToViewEntity(definition?.entity);

  const customizedTitle =
    title ||
    definition?.variant ||
    t(`COMMON.${definition?.entity.toUpperCase()}`, {
      count: 2,
    });

  const requestData = useMemo<DataRequest>(() => {
    return {
      filter,
      entity: viewEntity,
      data: { id: null, ...widgetDataByEntity[viewEntity] },
      sort: widgetSortOptionsByEntity[viewEntity],
    };
  }, [filter, viewEntity]);

  const originalResponse = useDataProvider(requestData, false, pageSize);
  const response = usePatchedDefaultData(originalResponse, pageSize);
  const fetchMore = response.fetchMore;

  const { handleClick, ...popoverState } = usePopoverState('bottom');

  const navigate = useNavigate();

  const listHrefEntitiy = useMemo(() => {
    if (definition?.variant) return `${viewEntity}(${definition?.variant})`;

    return viewEntity;
  }, [definition?.variant, viewEntity]);

  const handleFetchMore = useCallback(() => {
    fetchMore(response.data.length);
  }, [fetchMore, response.data.length]);

  const isNavigating = useRef(false);

  return (
    <>
      <Popover
        {...popoverState}
        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
      >
        <List disablePadding>
          {onEditClick && (
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  onEditClick();
                  popoverState.onClose();
                }}
              >
                <ListItemText primary={t('SETTINGS.SETTINGS')} />
              </ListItemButton>
            </ListItem>
          )}
          {onDeleteClick && (
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  onDeleteClick();
                  popoverState.onClose();
                }}
              >
                <ListItemText primary={t('COMMON.REMOVE')} />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Popover>
      <CardWidget
        entity={viewEntity}
        columns={widgetColsByEntity[viewEntity]}
        getItemHref={() => ''}
        listHref={`/more/entity/${listHrefEntitiy}?${searchParams.toString()}`}
        newHref={`/more/entity/${viewEntity}/details/new`}
        items={response.data}
        title={customizedTitle || t(`COMMON.${viewEntity.toUpperCase()}_other`)}
        totalCount={originalResponse.total}
        fetchMore={handleFetchMore}
        collapsible={true}
        collapsed={collapsed}
        onItemClick={(item) => {
          // Prevent double navigation
          if (!isNavigating.current) {
            isNavigating.current = true;
            setTimeout(() => {
              // Wait until one either double clicks or clicks the item to avoid mask closing
              navigate(`/more/entity/${viewEntity}/details/${item.id}`);
              isNavigating.current = false;
            }, 200);
          }
        }}
        onCollapsedChange={onCollapsedChange}
        additionalActions={
          <IconButton size="medium" onClick={handleClick}>
            <MoreVertIcon />
          </IconButton>
        }
      />
    </>
  );
};
